import { useEffect, useState } from 'react';
import { useLottie } from 'lottie-react';
import { useTranslation } from 'react-i18next';
import loadingAnimation from '../../../assets/loading/loading.json';
import { usePoseWorkerStore } from '../../../zustand/pose-worker-store';
import 'twin.macro';

// Combined size of what actually has to download before the pose model is
// ready: the WASM binary + its JS glue (public/wasm) and the heavy pose
// landmarker model (public/models/pose_landmarker_heavy.task).
const WASM_BYTES = 9067661 + 322252;
const MODEL_BYTES = 30664242;
const TOTAL_ASSET_BYTES = WASM_BYTES + MODEL_BYTES;

// WASM compile + model parse time on top of the raw download, roughly
// constant regardless of connection speed.
const PROCESSING_OVERHEAD_MS = 1500;
const MIN_ESTIMATED_DURATION_MS = 3000;
const MAX_ESTIMATED_DURATION_MS = 45000;
// Used only when the Network Information API isn't available at all
// (Safari/Firefox) — a mid-range broadband guess.
const FALLBACK_DOWNLINK_MBPS = 10;
// navigator.connection.downlink isn't exposed on every browser/OS even when
// the API exists — fall back to a per-effectiveType speed guess.
const EFFECTIVE_TYPE_DOWNLINK_MBPS = {
  'slow-2g': 0.05,
  '2g': 0.25,
  '3g': 0.7,
  '4g': 10,
};

function resolveDownlinkMbps() {
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!connection) {
    return FALLBACK_DOWNLINK_MBPS;
  }
  if (typeof connection.downlink === 'number' && connection.downlink > 0) {
    return connection.downlink;
  }
  return EFFECTIVE_TYPE_DOWNLINK_MBPS[connection.effectiveType] ?? FALLBACK_DOWNLINK_MBPS;
}

// The WASM fileset (loaded before the model) gives no byte-level progress, so
// that phase still relies on a time-based estimate — capped low since WASM is
// a small share of the total. The model phase below uses real bytes.
function resolveEstimatedDurationMs() {
  const downlinkMbps = resolveDownlinkMbps();
  const downloadMs = ((TOTAL_ASSET_BYTES * 8) / (downlinkMbps * 1_000_000)) * 1000;
  const estimatedMs = downloadMs + PROCESSING_OVERHEAD_MS;
  return Math.min(MAX_ESTIMATED_DURATION_MS, Math.max(MIN_ESTIMATED_DURATION_MS, estimatedMs));
}

const TICK_MS = 200;
// The WASM phase animates the bar up to this cap; the model download (real
// byte progress) then drives it from here to 100%.
const WASM_PHASE_MAX_PROGRESS = 20;

function useQueuedSeconds(queued) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    setSeconds(0);
    if (!queued) {
      return undefined;
    }
    const startedAt = Date.now();
    const intervalId = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - startedAt) / 1000)),
      1000
    );
    return () => window.clearInterval(intervalId);
  }, [queued]);
  return seconds;
}

const minutesSeconds = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const AvocatoLoading = ({ text }) => {
  const { t } = useTranslation('exercise');
  const progressState = usePoseWorkerStore(state => state.progress);
  const [wasmProgress, setWasmProgress] = useState(0);
  const [estimatedDurationMs] = useState(resolveEstimatedDurationMs);

  const { View } = useLottie(
    { animationData: loadingAnimation, loop: true, autoplay: true },
    { width: '100%', height: '100%' }
  );

  const hasRealProgress = progressState.phase === 'model' && progressState.total > 0;
  // The remote pose service has no free place: LiveKit sends it in once one frees (livekit-pose-runtime.js).
  const queued = progressState.phase === 'queue';
  const queuedSeconds = useQueuedSeconds(queued);

  // Estimated animation only runs during the WASM phase (no real byte signal).
  useEffect(() => {
    if (hasRealProgress) {
      return undefined;
    }
    const startedAt = performance.now();
    const intervalId = window.setInterval(() => {
      const elapsedMs = performance.now() - startedAt;
      setWasmProgress(
        Math.min(WASM_PHASE_MAX_PROGRESS, Math.round((elapsedMs / estimatedDurationMs) * 100))
      );
    }, TICK_MS);

    return () => window.clearInterval(intervalId);
  }, [estimatedDurationMs, hasRealProgress]);

  // During the model phase map real bytes onto the WASM_PHASE_MAX..100 range so
  // the bar continues smoothly from where the WASM estimate left off. Clamp to
  // 100 — when the server sends the model gzip/br-compressed, Content-Length
  // (compressed) is smaller than the decompressed bytes the reader yields, so
  // loaded/total can exceed 1 and push the bar past 100% (seen on iOS Safari).
  const progress = hasRealProgress
    ? Math.min(
        100,
        WASM_PHASE_MAX_PROGRESS +
          Math.round((progressState.loaded / progressState.total) * (100 - WASM_PHASE_MAX_PROGRESS))
      )
    : wasmProgress;

  const remainingSeconds = Math.max(
    1,
    Math.ceil(((100 - progress) / 100) * (estimatedDurationMs / 1000))
  );

  if (queued) {
    return (
      <div
        tw="flex flex-col justify-center items-center w-screen h-screen gap-3 bg-white px-6 text-center"
        role="status"
      >
        <div tw="h-[200px] w-[200px]">{View}</div>
        <p tw="text-xl font-bold">{t('avocatoLoading.busyTitle')}</p>
        <p tw="text-sm text-gray-500 max-w-[420px]">{t('avocatoLoading.busyText')}</p>
        <p tw="text-sm text-gray-500">
          {t('avocatoLoading.busyWaited', {
            elapsed: minutesSeconds(queuedSeconds),
            max: Math.round(progressState.total / 60000),
          })}
        </p>
      </div>
    );
  }

  return (
    <div tw="flex flex-col justify-center items-center w-screen h-screen gap-3 bg-white">
      <div tw="h-[200px] w-[200px]">{View}</div>
      <p tw="text-xl font-bold">{t('avocatoLoading.titlePrefix')}</p>
      <p tw="text-xl font-bold">{text ? text : t('page.loadingExercises')}</p>
      <div tw="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
        <div
          tw="h-full bg-[#8DA12B] rounded-full"
          style={{ width: `${progress}%`, transition: `width ${TICK_MS}ms linear` }}
        />
      </div>
      <p tw="text-sm text-gray-500">
        {t('avocatoLoading.statusRemaining', { progress, seconds: remainingSeconds })}
      </p>
    </div>
  );
};
export default AvocatoLoading;
